import React, { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextareaAutosize, FormControl, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface FormInputTextAreaProps {
  name: string;
  label?: string;
  placeholder?: string;
  className: string;
  rows: number;
  isRequired?: boolean;
  minLength?: number;
  maxLength?: number;
  fieldHasName?: boolean;
}

const defaultProps = {
  label: '',
  placeholder: '',
  isRequired: false,
  minLength: 0,
  maxLength: Infinity,
  fieldHasName: false,
};

export const FormInputTextArea: FC<FormInputTextAreaProps> = ({
  name,
  label,
  placeholder,
  rows,
  className,
  isRequired,
  minLength,
  maxLength,
  fieldHasName,
  ...props
}) => {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const validationRules = {
    required: {
      value: isRequired || defaultProps.isRequired,
      message: t('validation.thisFieldIsRequired'),
    },
    minLength: {
      value: minLength || defaultProps.minLength,
      message: `${t('validation.theNumberOfCharactersMustBeAtLeast')} ${minLength}`,
    },
    validate: (value = '') => {
      return (
        value.trim().length <= (maxLength || defaultProps.maxLength) ||
        (t('validation.exceededPermittedNumberOfCharacters') as string)
      );
    },
  };

  return (
    <FormControl component="fieldset">
      {label && <Typography component="legend">{label}</Typography>}
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <TextareaAutosize
              onChange={onChange}
              minRows={rows}
              maxRows={rows}
              name={fieldHasName ? name : undefined}
              value={value || ''}
              className={`textarea textarea__${className} ${error ? 'textarea__error' : ''}`}
              placeholder={placeholder || undefined}
              {...props}
            />
            {error ? <Typography component="p">{error.message}</Typography> : null}
          </>
        )}
      />
    </FormControl>
  );
};

FormInputTextArea.defaultProps = defaultProps;
